import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Field, Select } from '../components/Field'
import { Button } from '../components/Button'
import { PubBadge } from '../components/PubBadge'
import { api, ApiError } from '../api/client'

type SceneAsset = {
  idx: number
  shot_type: string | null
  still: string | null
  clip: string | null
  audio: string | null
  social_card_ig_square: string | null
  narration: string | null
  caption: string | null
  title_text: string | null
  subtitle_text: string | null
}

type SocialCards = {
  hero_twitter_169?: string | null
  hero_ig_square?: string | null
  hero_ig_story?: string | null
}

type TrailerAssets = {
  base_url: string
  trailer_169: string | null
  trailer_916: string | null
  trailer_11: string | null
  poster: string | null
  script_url: string | null
  captions_url: string | null
  social: SocialCards | null
  trailer_title: string | null
  scenes: SceneAsset[]
}

type TrailerRow = {
  article_id: number
  slug: string | null
  title: string
  publication_slug: string
  has_trailer: boolean
  trailer_published: boolean
  trailer_url: string | null
  trailer_poster_url: string | null
  trailer_duration_seconds: number | null
  trailer_disclosure: string | null
  trailer_generated_at: string | null
  trailer_first_play_at: string | null
  trailer_assets: TrailerAssets | null
}

type TrailerListResponse = {
  items: TrailerRow[]
  total: number
  page: number
  per_page: number
}

type FilterMode = 'all' | 'with' | 'without'

const DEFAULT_DISCLOSURE =
  'AI-generated editorial visualization. The narration and visuals were produced by AI from this Flight Journal article. The article itself remains the source of record.'

// AI disclosure is OPT-IN. Default to empty so attaching a trailer
// doesn't auto-stamp the "AI-generated" line on the article page.
// The form exposes a "Use default copy" button that pastes the
// editorial-approved language if the editor wants to enable it.

export function TrailersPage() {
  const [q, setQ] = useState('')
  const [pubSlug, setPubSlug] = useState<string>('')
  const [mode, setMode] = useState<FilterMode>('with')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<TrailerRow | null>(null)
  const [previewing, setPreviewing] = useState<TrailerRow | null>(null)

  const qc = useQueryClient()
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  if (pubSlug) params.set('publication_slug', pubSlug)
  if (mode !== 'all') params.set('has_trailer', mode === 'with' ? 'true' : 'false')
  params.set('page', String(page))
  params.set('per_page', '25')

  const { data, isLoading } = useQuery<TrailerListResponse>({
    queryKey: ['admin-trailers', q, pubSlug, mode, page],
    queryFn: () => api.get<TrailerListResponse>(`/api/admin/trailers?${params.toString()}`),
    staleTime: 5_000,
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-trailers'] })

  const detach = useMutation({
    mutationFn: (articleId: number) =>
      api.del<TrailerRow>(`/api/admin/trailers/${articleId}`),
    onSuccess: () => refresh(),
  })

  const publish = useMutation({
    mutationFn: (articleId: number) =>
      api.post<TrailerRow>(`/api/admin/trailers/${articleId}/publish`),
    onSuccess: () => refresh(),
  })

  const unpublish = useMutation({
    mutationFn: (articleId: number) =>
      api.post<TrailerRow>(`/api/admin/trailers/${articleId}/unpublish`),
    onSuccess: () => refresh(),
  })

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">AI Pipeline · Editorial Surface</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Article Trailers
          </h1>
          <p className="text-paper-dim text-sm mt-2 max-w-2xl">
            Attach AI-generated cinematic trailers to articles. Generation happens
            offline via <code className="font-mono text-amber-glow">scripts/generate_trailer.py</code>;
            this surface manages the resulting metadata + which articles publish trailers.
          </p>
        </div>
        <div className="text-right">
          <div className="label">Matching</div>
          <div className="font-mono text-2xl text-amber-glow tabular-nums mt-0.5">
            {data?.total ?? '—'}
          </div>
        </div>
      </header>

      <Panel label="Query" accent="amber">
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-5">
            <Field
              label="Search (title or slug)"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value) }}
              placeholder="quarterhorse, ploesti, wildcat..."
            />
          </div>
          <div className="col-span-3">
            <Select
              label="Publication"
              value={pubSlug}
              onChange={(e) => { setPage(1); setPubSlug(e.target.value) }}
              options={[
                { value: '', label: 'All' },
                { value: 'flight_journal', label: 'Flight Journal' },
                { value: 'diecast', label: 'Die Cast X' },
                { value: 'model_airplane_news', label: 'Model Airplane News' },
                { value: 'rc_car_action', label: 'RC Car Action' },
              ]}
            />
          </div>
          <div className="col-span-3">
            <Select
              label="Trailer status"
              value={mode}
              onChange={(e) => { setPage(1); setMode(e.target.value as FilterMode) }}
              options={[
                { value: 'with', label: 'Has trailer' },
                { value: 'without', label: 'No trailer' },
                { value: 'all', label: 'All' },
              ]}
            />
          </div>
        </div>
      </Panel>

      <Panel label="Results" bodyClassName="p-0">
        {isLoading && (
          <div className="px-4 py-12 text-center label text-paper-trace">Loading…</div>
        )}
        {!isLoading && (data?.items.length ?? 0) === 0 && (
          <div className="px-4 py-12 text-center label text-paper-trace">
            No matches. Try a different filter or attach a trailer to an unmatched article.
          </div>
        )}
        {!isLoading && (data?.items.length ?? 0) > 0 && (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>Article</Th>
                <Th className="w-28">Publication</Th>
                <Th className="w-24">Status</Th>
                <Th className="w-20">Duration</Th>
                <Th className="w-28">Generated</Th>
                <Th className="w-28">First Play</Th>
                <Th className="w-56 text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {data!.items.map((row) => (
                <tr key={row.article_id} className="border-b border-ink-600 hover:bg-ink-800">
                  <Td>
                    <div className="font-serif text-[15px] text-paper leading-tight">
                      {row.title}
                    </div>
                    <div className="font-mono text-[10px] text-paper-trace mt-1">
                      #{row.article_id} · {row.slug ?? '(no slug)'}
                    </div>
                  </Td>
                  <Td>
                    <PubBadge slug={row.publication_slug} />
                  </Td>
                  <Td>
                    {row.has_trailer ? (
                      row.trailer_published ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-label
                                         border border-amber-glow text-amber-glow bg-amber-glow/10">
                          Published
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-label
                                         border border-paper-trace text-paper-trace bg-paper-trace/5">
                          Draft
                        </span>
                      )
                    ) : (
                      <span className="text-paper-trace text-[11px]">—</span>
                    )}
                  </Td>
                  <Td>
                    {row.trailer_duration_seconds != null ? (
                      <span className="font-mono tabular-nums">
                        {row.trailer_duration_seconds.toFixed(1)}s
                      </span>
                    ) : (
                      <span className="text-paper-trace">—</span>
                    )}
                  </Td>
                  <Td>
                    {row.trailer_generated_at ? (
                      <span className="font-mono text-[11px] tabular-nums text-paper-dim">
                        {new Date(row.trailer_generated_at).toLocaleDateString()}
                      </span>
                    ) : <span className="text-paper-trace">—</span>}
                  </Td>
                  <Td>
                    {row.trailer_first_play_at ? (
                      <span className="font-mono text-[11px] tabular-nums text-amber-glow">
                        {new Date(row.trailer_first_play_at).toLocaleDateString()}
                      </span>
                    ) : <span className="text-paper-trace">never</span>}
                  </Td>
                  <Td className="text-right pr-4">
                    <div className="flex justify-end gap-2 flex-wrap">
                      {row.has_trailer && (
                        <button
                          type="button"
                          className="text-xs font-mono text-amber-glow hover:text-paper underline"
                          onClick={() => setPreviewing(row)}
                        >preview</button>
                      )}
                      {row.has_trailer && !row.trailer_published && (
                        <button
                          type="button"
                          className="text-xs font-mono text-amber-glow hover:text-paper underline"
                          title="Show the Watch Trailer button on the article page"
                          onClick={() => publish.mutate(row.article_id)}
                          disabled={publish.isPending}
                        >publish</button>
                      )}
                      {row.has_trailer && row.trailer_published && (
                        <button
                          type="button"
                          className="text-xs font-mono text-paper-dim hover:text-paper underline"
                          title="Hide the Watch Trailer button without detaching the assets"
                          onClick={() => unpublish.mutate(row.article_id)}
                          disabled={unpublish.isPending}
                        >unpublish</button>
                      )}
                      <button
                        type="button"
                        className="text-xs font-mono text-paper hover:text-amber-glow underline"
                        onClick={() => setEditing(row)}
                      >{row.has_trailer ? 'edit' : 'attach'}</button>
                      {row.has_trailer && (
                        <button
                          type="button"
                          className="text-xs font-mono text-red-400 hover:text-red-200 underline"
                          onClick={() => {
                            if (confirm(`Detach trailer from "${row.title}"?\n\nThe mp4 stays on the CDN, only the article binding is removed.`)) {
                              detach.mutate(row.article_id)
                            }
                          }}
                        >detach</button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm font-mono text-paper-dim">
          <button
            type="button"
            className="hover:text-amber-glow disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >← Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button
            type="button"
            className="hover:text-amber-glow disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >Next →</button>
        </div>
      )}

      {editing && (
        <EditModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh() }}
        />
      )}
      {previewing && (
        <PreviewModal
          row={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  )
}


// -- Edit / attach modal ----------------------------------------------

function EditModal({
  row, onClose, onSaved,
}: {
  row: TrailerRow
  onClose: () => void
  onSaved: () => void
}) {
  const [trailerUrl, setTrailerUrl] = useState(row.trailer_url ?? '')
  const [posterUrl, setPosterUrl] = useState(row.trailer_poster_url ?? '')
  const [duration, setDuration] = useState<string>(
    row.trailer_duration_seconds != null ? row.trailer_duration_seconds.toFixed(2) : ''
  )
  // Default to empty (= no disclosure) per the opt-in model.
  // Pre-fill from the existing record if one is already set.
  const [disclosure, setDisclosure] = useState(row.trailer_disclosure ?? '')
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => api.put<TrailerRow>(`/api/admin/trailers/${row.article_id}`, {
      trailer_url: trailerUrl.trim(),
      trailer_poster_url: posterUrl.trim() || null,
      trailer_duration_seconds: duration ? Number(duration) : null,
      trailer_disclosure: disclosure.trim() || null,
    }),
    onSuccess: () => onSaved(),
    onError: (e: unknown) => {
      const msg = e instanceof ApiError
        ? (typeof e.detail === 'object' && e.detail && 'detail' in e.detail
            ? String((e.detail as { detail: unknown }).detail)
            : `HTTP ${e.status}`)
        : 'Save failed'
      setError(msg)
    },
  })

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/80 flex items-center justify-center p-6">
      <div className="bg-ink-900 border border-ink-500 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-4 border-b border-ink-500">
          <div className="label">Trailer · Edit</div>
          <h2 className="font-serif text-2xl text-paper mt-1 leading-tight">{row.title}</h2>
          <div className="font-mono text-[11px] text-paper-trace mt-1">
            #{row.article_id} · {row.slug ?? '(no slug)'}
          </div>
        </header>
        <div className="px-5 py-5 space-y-4">
          <Field
            label="Trailer URL (mp4)"
            value={trailerUrl}
            onChange={(e) => setTrailerUrl(e.target.value)}
            placeholder="https://cdn.flightjournal.com/trailers/<slug>-v3-3-timing-fixed.mp4"
          />
          <Field
            label="Poster URL (jpg, optional but recommended)"
            value={posterUrl}
            onChange={(e) => setPosterUrl(e.target.value)}
            placeholder="https://cdn.flightjournal.com/trailers/<slug>-v3-3-poster.jpg"
          />
          <Field
            label="Duration (seconds)"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="42.40"
            type="number"
            step="0.01"
          />
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className="label">AI Disclosure (optional)</label>
              {!disclosure && (
                <button
                  type="button"
                  className="text-[11px] font-mono text-amber-glow hover:text-paper underline"
                  onClick={() => setDisclosure(DEFAULT_DISCLOSURE)}
                >use default copy</button>
              )}
              {disclosure && (
                <button
                  type="button"
                  className="text-[11px] font-mono text-paper-trace hover:text-paper underline"
                  onClick={() => setDisclosure('')}
                >clear</button>
              )}
            </div>
            <textarea
              value={disclosure}
              onChange={(e) => setDisclosure(e.target.value)}
              rows={3}
              placeholder="Leave empty to skip the disclosure paragraph on the article page."
              className="w-full bg-ink-950 border border-ink-500 px-3 py-2
                         text-paper text-sm font-serif leading-relaxed
                         focus:outline-none focus:border-amber-glow"
            />
            <div className="text-[11px] text-paper-trace mt-1">
              Optional. Empty by default — no AI disclosure shown on the article
              page. Click <em>use default copy</em> to insert the editorially-approved
              language, or write your own.
            </div>
          </div>
          {error && (
            <div className="border border-red-500/50 bg-red-950/40 px-3 py-2 text-sm text-red-300 font-mono">
              {error}
            </div>
          )}
        </div>
        <footer className="px-5 py-4 border-t border-ink-500 flex justify-end gap-3">
          <button
            type="button"
            className="text-sm font-mono text-paper-dim hover:text-paper"
            onClick={onClose}
          >cancel</button>
          <Button
            onClick={() => { setError(null); save.mutate() }}
            disabled={!trailerUrl.trim() || save.isPending}
          >
            {save.isPending ? 'saving…' : (row.has_trailer ? 'update' : 'attach')}
          </Button>
        </footer>
      </div>
    </div>
  )
}


// -- Preview modal ----------------------------------------------------

function PreviewModal({
  row, onClose,
}: {
  row: TrailerRow
  onClose: () => void
}) {
  const [tab, setTab] = useState<'player' | 'assets'>('player')

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/80 flex items-center justify-center p-6"
         onClick={onClose}>
      <div className="bg-ink-900 border border-ink-500 max-w-4xl w-full max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-4 border-b border-ink-500 flex items-start justify-between gap-4">
          <div>
            <div className="label">Trailer · Preview</div>
            <h2 className="font-serif text-2xl text-paper mt-1 leading-tight">{row.title}</h2>
            <div className="font-mono text-[11px] text-paper-trace mt-1">
              #{row.article_id} · {row.trailer_duration_seconds?.toFixed(1) ?? '—'}s
            </div>
          </div>
          <button
            type="button"
            className="text-paper-dim hover:text-paper text-xl leading-none"
            onClick={onClose}
            aria-label="Close"
          >×</button>
        </header>

        {row.trailer_assets && (
          <nav className="px-5 pt-4 flex gap-4 border-b border-ink-500">
            <button
              type="button"
              className={`label pb-2 -mb-px border-b-2 ${
                tab === 'player' ? 'border-amber-glow text-amber-glow' : 'border-transparent text-paper-dim hover:text-paper'
              }`}
              onClick={() => setTab('player')}
            >Player</button>
            <button
              type="button"
              className={`label pb-2 -mb-px border-b-2 ${
                tab === 'assets' ? 'border-amber-glow text-amber-glow' : 'border-transparent text-paper-dim hover:text-paper'
              }`}
              onClick={() => setTab('assets')}
            >Assets · {row.trailer_assets.scenes.length} scenes</button>
          </nav>
        )}

        {tab === 'player' && (
          <div className="p-5 space-y-3">
            {row.trailer_url && (
              <video
                src={row.trailer_url}
                poster={row.trailer_poster_url ?? undefined}
                controls
                autoPlay
                playsInline
                className="w-full aspect-video bg-ink-950"
              />
            )}
            {row.trailer_disclosure && (
              <div className="text-sm text-paper-dim leading-relaxed">
                <span className="label">AI disclosure</span>{' '}
                <span className="font-serif">{row.trailer_disclosure}</span>
              </div>
            )}
          </div>
        )}

        {tab === 'assets' && row.trailer_assets && (
          <AssetsTab assets={row.trailer_assets} />
        )}
      </div>
    </div>
  )
}


function AssetsTab({ assets }: { assets: TrailerAssets }) {
  return (
    <div className="p-5 space-y-6">
      <div>
        <div className="label mb-2">Trailer · Aspect ratio variants</div>
        <div className="space-y-2">
          <CopyRow label="16:9 (web / article page)" url={assets.trailer_169} />
          <CopyRow label="9:16 (TikTok / Reels / Shorts)" url={assets.trailer_916} />
          <CopyRow label="1:1 (Instagram feed)" url={assets.trailer_11} />
          <CopyRow label="Poster (still hero image)" url={assets.poster} />
        </div>
      </div>

      <div>
        <div className="label mb-2">Copy · Script + captions</div>
        <div className="space-y-2">
          <CopyRow label="Full narration script" url={assets.script_url} />
          <CopyRow label="Captions (one per scene)" url={assets.captions_url} />
        </div>
      </div>

      {assets.social && (
        <div>
          <div className="label mb-2">Social · Auto-generated share cards</div>
          <p className="text-[11px] text-paper-trace mb-3">
            Each card has the trailer title + subtitle composited over a clean
            still, with the FLJ wordmark — paste-ready for the matching platform.
          </p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            {assets.social.hero_twitter_169 && (
              <SocialCardTile label="Twitter / X (1200×675)" url={assets.social.hero_twitter_169} />
            )}
            {assets.social.hero_ig_square && (
              <SocialCardTile label="Instagram feed (1080×1080)" url={assets.social.hero_ig_square} />
            )}
            {assets.social.hero_ig_story && (
              <SocialCardTile label="IG story / Reels (1080×1920)" url={assets.social.hero_ig_story} />
            )}
          </div>
        </div>
      )}

      <div>
        <div className="label mb-2">Per-scene assets</div>
        <p className="text-[11px] text-paper-trace mb-3">
          Each scene has a still (gpt-image-2 — usable as a social hero image),
          a Veo motion clip (8s, usable as a standalone teaser),
          and the cedar narration audio (usable in podcasts or as separate audio).
        </p>
        <div className="space-y-4">
          {assets.scenes.map((s) => (
            <div key={s.idx} className="border border-ink-600 bg-ink-950 p-3">
              <div className="flex items-baseline justify-between mb-2">
                <span className="font-mono text-[11px] tabular-nums text-amber-glow">
                  #{s.idx} · {s.shot_type}
                </span>
                {s.title_text && (
                  <span className="font-serif text-sm text-paper">{s.title_text}</span>
                )}
              </div>
              {s.narration && (
                <div className="text-sm text-paper-dim mb-3 italic">
                  "{s.narration}"
                </div>
              )}
              {s.caption && (
                <div className="text-[11px] text-paper-trace mb-3">
                  Caption: <span className="text-paper-dim">{s.caption}</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                {s.still && (
                  <div>
                    <img src={s.still} alt={`scene ${s.idx} still`}
                         className="w-full aspect-video object-cover bg-ink-900 mb-1" />
                    <CopyRow label="Still (PNG)" url={s.still} compact />
                  </div>
                )}
                {s.clip && (
                  <div>
                    <video src={s.clip} controls preload="metadata" muted
                           className="w-full aspect-video bg-ink-900 mb-1" />
                    <CopyRow label="Clip (MP4, 8s)" url={s.clip} compact />
                  </div>
                )}
                {s.audio && (
                  <div>
                    <audio src={s.audio} controls preload="metadata"
                           className="w-full mb-1" />
                    <CopyRow label="Audio (MP3)" url={s.audio} compact />
                  </div>
                )}
              </div>
              {s.social_card_ig_square && (
                <div className="mt-3 pt-3 border-t border-ink-700">
                  <div className="flex items-center gap-3">
                    <img src={s.social_card_ig_square}
                         alt={`scene ${s.idx} social card`}
                         className="w-16 h-16 object-cover bg-ink-900 border border-ink-600" />
                    <div className="flex-1">
                      <div className="text-[10px] text-paper-trace mb-1">
                        Scene social card (1080×1080)
                      </div>
                      <CopyRow
                        label="ig_square"
                        url={s.social_card_ig_square}
                        compact
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}


function SocialCardTile({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="space-y-1">
      <a href={url} target="_blank" rel="noopener" className="block">
        <img src={url} alt={label}
             className="w-full bg-ink-900 border border-ink-600 hover:border-amber-glow" />
      </a>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-paper-dim">{label}</span>
        <button
          type="button"
          className="font-mono text-amber-glow hover:text-paper underline"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url)
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            } catch {
              window.prompt('copy this URL', url)
            }
          }}
        >{copied ? 'copied!' : 'copy url'}</button>
      </div>
    </div>
  )
}


function CopyRow({
  label, url, compact = false,
}: {
  label: string
  url: string | null
  compact?: boolean
}) {
  const [copied, setCopied] = useState(false)
  if (!url) {
    return (
      <div className={`flex items-center justify-between ${compact ? 'text-[10px]' : 'text-xs'} text-paper-trace`}>
        <span>{label}</span>
        <span>—</span>
      </div>
    )
  }
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      window.prompt('copy this URL', url)
    }
  }
  return (
    <div className={`flex items-center justify-between gap-3 ${compact ? 'text-[10px]' : 'text-xs'}`}>
      <span className={`${compact ? 'text-paper-dim' : 'label'} shrink-0`}>{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        <a href={url} target="_blank" rel="noopener"
           className="font-mono text-paper-dim hover:text-amber-glow truncate max-w-[280px]">
          {url.split('/').slice(-2).join('/')}
        </a>
        <button
          type="button"
          className="font-mono text-amber-glow hover:text-paper underline shrink-0"
          onClick={onCopy}
        >{copied ? 'copied!' : 'copy'}</button>
      </div>
    </div>
  )
}


// -- table cell helpers ----------------------------------------------

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`label text-left px-4 py-2 ${className}`}>{children}</th>
  )
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
  )
}
